// @flow
import React, {Fragment} from 'react';
import {Link} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  IconButton,
  Grid,
  Hidden,
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Snackbar,
} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import SnackBar from '../../../../../elements/SnackBar/SnackBar';
import {Alert} from '@material-ui/lab';
import {ArrowBack} from '@material-ui/icons';

import type {Store} from '../../../../../entities/index';
import {useIsMinimalPlan} from '../../../../store/utils';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    margin: '.75rem',
  },
  description: {
    color: 'grey',
    fontSize: '14px',
  },
  subtitle: {
    color: colors.grey900,
  },
  gridIcon: {
    margin: 'auto',
    textAlign: 'center',
  },
  icon: {
    backgroundColor: colors.purple1,
    borderRadius: '15px',
    padding: '4px',
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1, 0),
  },
  backButton: {
    margin: 'auto',
    padding: theme.spacing(1, 4),
  },
  paper: {
    padding: theme.spacing(1),
  },
  snackBarSuccess: {
    backgroundColor: '#E5F7F2',
    color: '#00B282',
  },
  snackBarError: {
    backgroundColor: '#FDECEA',
    color: '#A05536',
  },
  subRouteHeaderSmall: {
    display: 'flex',
    padding: '20px',
    alignItems: 'center',
    margin: 'auto',
    justifyContent: 'center',
    backgroundColor: '#F8F9FB',
  },
  subRouteTitle: {
    marginLeft: theme.spacing(2),
    fontSize: '1rem',
    fontWeight: 600,
  },
  linkButton: {
    fontWeight: 600,
    textDecoration: 'none',
  },
}));

type Props = {
  handleToggleEnabled: () => void,
  enabled: boolean,
  isSaving?: boolean,
  error?: string,
  shippingError?: boolean,
  updateSuccess?: boolean,
  store: Store,
};

export default function PayOnDelivery(props: Props) {
  const {
    handleToggleEnabled,
    error,
    shippingError,
    updateSuccess,
    enabled,
    isSaving,
  } = props;
  const classes = useStyles();

  const isMinimal = useIsMinimalPlan();

  return (
    <Fragment>
      <Snackbar open={shippingError} autoHideDuration={4000}>
        <Alert severity="info">
          Para desactivar esta opción debes desactivar: Permitir envíos fuera de
          la zona delivery.{' '}
          <Link
            to="/configure/logistics/shipping/advanced"
            className={classes.linkButton}
          >
            Clic aquí.
          </Link>
        </Alert>
      </Snackbar>
      <div className={classes.subRouteHeader}>
        <Hidden xsDown>
          <IconButton
            variant="contained"
            size="small"
            component={Link}
            to="/configure/payment-methods"
          >
            <ArrowBack />
          </IconButton>
        </Hidden>
        <Typography
          component="h6"
          variant="h6"
          className={classes.subRouteTitle}
        >
          Pago contra entrega
        </Typography>
      </div>
      <Grid
        container
        spacing={3}
        alignContent="flex-start"
        className={classes.paper}
      >
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControlLabel
            classes={{label: classes.checkboxLabel}}
            control={
              <Checkbox
                checked={enabled}
                onChange={handleToggleEnabled}
                color="primary"
                disabled={isSaving || isMinimal}
              />
            }
            label="Activar el método de pago"
          />
        </Grid>

        <Grid item xs={12} sm={12} md={11} lg={8}>
          <div className={classes.subRouterBody}>
            <Typography component="div" className={classes.subtitle}>
              <Box
                fontWeight={500}
                fontSize={14}
                className={classes.description}
              >
                Recibe los pedidos y coordina la entrega y el pago directamente
                al entregar el producto.
              </Box>
              <Box
                fontWeight={500}
                fontSize={14}
                className={classes.description}
              >
                Facilita la experiencia de compra para tus clientes
                ofreciéndoles un método confiable y directo.
              </Box>
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={11} lg={8}></Grid>
        <Hidden smUp>
          <div className={classes.subRouteHeaderSmall}>
            <Button
              size="large"
              color="primary"
              fullWidth
              component={Link}
              to="/configure/payment-methods"
              disabled={isSaving}
              startIcon={<ArrowBack />}
            >
              <Typography color="primary" className={classes.subRouteTitle}>
                Regresar
              </Typography>
            </Button>
          </div>
        </Hidden>
      </Grid>
      <SnackBar
        mode="error"
        className={classes.snackBarError}
        message={error ? 'Error al guardar los cambios' : undefined}
      />
      <SnackBar
        mode="success"
        className={classes.snackBarSuccess}
        message={
          updateSuccess ? 'Se guardaron los cambios exitosamente' : undefined
        }
      />
    </Fragment>
  );
}
